<script setup lang="ts">
import { computed, watch } from "vue";

import { useTypedIntl } from "@/locales/typed-intl";
import $date, { EDateScaling } from "@/utils/date";

import DatePicker from "primevue/datepicker";

interface IProps {
  fromDateError: string | undefined;
  toDateError: string | undefined;
}

const props = defineProps<IProps>();

const { t, k, rawT, locale } = useTypedIntl();

const fromDate = defineModel<Date | null>("fromDate", {
  required: false,
  default: new Date(),
});
const toDate = defineModel<Date | null>("toDate", {
  required: false,
  default: $date.add(new Date(), 1, EDateScaling.DAY),
});

const dateFormat = computed(() => {
  switch (locale.value) {
    case "en":
      return "MM dd yy";
    case "fr":
    default:
      return "dd MM yy";
  }
});

// Set fromTime to the correct date once fromDate changed
watch(fromDate, (newFromDateValue) => {
  if (!newFromDateValue || !toDate.value) return;

  // if fromDate is not after toDate, change toDate to the correct date
  if (newFromDateValue.valueOf() > toDate.value.valueOf()) {
    toDate.value = $date.clone(newFromDateValue);
  }
});
</script>

<template>
  <div class="flex flex-row items-baseline justify-start gap-2">
    <span>{{ t(k.global.misc.dateFrom) }}</span>
    <section class="flex flex-col">
      <DatePicker
        v-model="fromDate"
        class="w-[14rem]"
        data-test-id="from-date-picker"
        :date-format="dateFormat"
        :invalid="!!props.fromDateError"
        show-time
      />
      <small
        v-if="props.fromDateError"
        class="text-red-500"
      >
        {{ rawT(`veeValidate.messages.${props.fromDateError}`) }}
      </small>
    </section>
    <span>{{ t(k.global.misc.dateTo).toLowerCase() }}</span>
    <section class="flex flex-col">
      <DatePicker
        v-model="toDate"
        class="w-[14rem]"
        data-test-id="to-date-picker"
        :date-format="dateFormat"
        :invalid="!!props.toDateError"
        :min-date="fromDate ?? undefined"
        show-time
      />
      <small
        v-if="props.toDateError"
        class="text-red-500"
      >
        {{ rawT(`veeValidate.messages.${props.toDateError}`) }}
      </small>
    </section>
  </div>
</template>
